export type PagedResult<T> = {
  items: T[];
  totalPagesCount: number;
};

// 初回 + リトライ 3 回。React Query の default retry と同じ試行回数に揃える。
const MAX_ATTEMPTS_PER_PAGE = 4;

// React Query の default retryDelay と同じ指数バックオフ。
const retryDelayMs = (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30_000);

const sleep = (ms: number) => new Promise<void>((resolve) => { setTimeout(resolve, ms); });

/**
 * 1 ページ分を取得する。失敗したらそのページだけを上限回数まで再試行する。
 *
 * retry を fetchAllPages 全体でなくページ単位に持たせているのは、クエリ全体の
 * retry だと 1 ページの失敗で成功済みのページまで取り直してしまうため
 * （N ページ中 1 ページ失敗で最大 4×N リクエストになる）。
 * 呼び出し側の useQuery では retry: false にしてこの実装に一本化する。
 */
const fetchPageWithRetry = async <T>(
  fetchPage: (page: number) => Promise<PagedResult<T>>,
  page: number,
): Promise<PagedResult<T>> => {
  let lastError: unknown;

  for (let attempt = 0; attempt < MAX_ATTEMPTS_PER_PAGE; attempt += 1) {
    try {
      return await fetchPage(page);
    } catch (error) {
      lastError = error;
      if (attempt < MAX_ATTEMPTS_PER_PAGE - 1) {
        await sleep(retryDelayMs(attempt));
      }
    }
  }

  throw lastError;
};

/**
 * ページングされた API を全ページ取得して 1 配列に連結する。
 *
 * page 1 を取得して総ページ数を確定させたあと、2..N を並列に取得する。
 * 総ページ数は page 1 が返した値だけを採用し、2 ページ目以降が違う値を
 * 返しても追加のリクエストはしない（取得回数を決定的にするため）。
 *
 * ページ番号は 1 origin。0 origin の API は呼び出し側のアダプタで変換する。
 */
export const fetchAllPages = async <T>(
  fetchPage: (page: number) => Promise<PagedResult<T>>,
): Promise<T[]> => {
  const first = await fetchPageWithRetry(fetchPage, 1);
  // 0 は「空レスポンス」であって「page 1 を捨てる」ではないので 1 と同じ経路に流す
  if (first.totalPagesCount <= 1) return first.items;

  const restPageNumbers = Array.from(
    { length: first.totalPagesCount - 1 },
    (_, index) => index + 2,
  );
  const rest = await Promise.all(
    restPageNumbers.map((page) => fetchPageWithRetry(fetchPage, page)),
  );

  return [...first.items, ...rest.flatMap((result) => result.items)];
};
